import React from 'react'
import styled from 'styled-components'
import { Modal, Text } from '@pancakeswap-libs/uikit'
import { AbstractConnector } from '@web3-react/abstract-connector'
import useI18n from 'hooks/useI18n'
import useWalletOptions, { WalletOption } from 'hooks/useWalletOptions'

interface Props {
  onDismiss?: () => void
  onConnect: (connector: AbstractConnector, walletId: string) => void
}

const List = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
`

const WalletRow = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor ?? theme.colors.tertiary};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.invertedContrast};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  text-align: left;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.tertiary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

const IconSlot = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }
`

const FallbackIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.tertiary};
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const Footnote = styled(Text)`
  margin-top: 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
`

const WalletEntry: React.FC<{ option: WalletOption; onSelect: (option: WalletOption) => void }> = ({
  option,
  onSelect,
}) => (
  <WalletRow
    type="button"
    disabled={!option.available}
    onClick={() => option.available && onSelect(option)}
    aria-label={`Connect ${option.name}`}
  >
    <IconSlot>
      {option.iconUrl ? (
        <img src={option.iconUrl} alt="" aria-hidden="true" />
      ) : (
        <FallbackIcon aria-hidden="true">{option.name.charAt(0)}</FallbackIcon>
      )}
    </IconSlot>
    <Details>
      <Text bold>{option.name}</Text>
      {option.unavailableReason ? (
        <Text fontSize="12px" color="textSubtle">
          {option.unavailableReason}
        </Text>
      ) : (
        option.detected && (
          <Text fontSize="12px" color="textSubtle">
            Detected
          </Text>
        )
      )}
    </Details>
  </WalletRow>
)

/**
 * The app's own connect-wallet modal.
 *
 * This replaces the UIKit's `useWalletModal`, which renders a fixed roster of seven
 * wallets but only exposes three connector ids - so four of its tiles resolved to the
 * same generic injected provider and its WalletConnect tile did nothing at all. The
 * list here is built from wallets actually installed in the browser (EIP-6963), so
 * every entry connects to the wallet it names.
 */
const WalletModal: React.FC<Props> = ({ onDismiss, onConnect }) => {
  const TranslateString = useI18n()
  const options = useWalletOptions()

  const handleSelect = (option: WalletOption) => {
    if (!option.connector) return
    onConnect(option.connector, option.id)
    onDismiss?.()
  }

  const noneDetected = !options.some((option) => option.detected)

  return (
    <Modal title={TranslateString(1152, 'Connect to a wallet')} onDismiss={onDismiss}>
      <List>
        {options.map((option) => (
          <WalletEntry key={option.id} option={option} onSelect={handleSelect} />
        ))}
      </List>

      {noneDetected && (
        <Footnote>
          {TranslateString(
            1154,
            'No wallet extension detected in this browser. Install one, then reload the page.'
          )}
        </Footnote>
      )}
    </Modal>
  )
}

export default WalletModal
